/**
 * Hero — total trust value + area chart. Top section of the dashboard.
 * Ported from app.jsx (Hero component) in the Claude Design output.
 *
 * Phase 11: reads headline numbers from the live `/v1/dashboard/summary`
 * endpoint via the shared `lib/api` client. The chart series is now
 * wired to /v1/trusts/{trustId}/aum-snapshots — populated by the daily
 * aum-snapshot-aggregator timer FA. Until enough snapshots accumulate,
 * the chart surfaces an honest "Chart appears after the first nightly
 * snapshot run" message rather than rendering a fixture line.
 *
 * 2026-06-10 (XCUT-07 / DASH-01): the fabricated "JSE Top 40" benchmark
 * line + toggle was removed — there is no market-data feed wired, so the
 * dashed series and its legend were unverifiable invented data on a
 * regulator-facing surface. The range tabs are constrained to the windows
 * the snapshot series genuinely covers (we request 24 monthly snapshots),
 * so the previously no-op 5Y / All tabs are gone.
 */

import { Fragment, useState } from 'react';
import { displayLegalName } from '../../lib/format/legal-name';
import { Link } from '@tanstack/react-router';
import { Icon } from '../icon';
import { GrowthChart, type GrowthPoint } from '../charts/growth-chart';
import { Skeleton } from '../ui/skeleton';
import { resolveGrowthDisplay } from './growth-series';
import { ValueProvenance, formatAsAtShort } from '../value-provenance';
import { fmtR, formatRand, NNBSP } from '../../lib/fmt-r';
import { useGrants } from '../../lib/api/grants';
import { mapError } from '../../lib/error-messages';
import { useHiddenFromActingFirm } from '../../lib/authz/act-as-nav';
import {
  estateSplitParts,
  useAumSnapshots,
  useDashboardSummary,
  useTrusts,
  type AumSnapshotView,
} from '../../lib/api';

// The AUM-snapshot series is requested with `limit: 24` (24 monthly marks),
// so only windows up to ~1 year of trailing data are genuinely backed by
// real snapshots. Longer windows (5Y / All) would have rendered the same
// data under a misleading label, so they are intentionally omitted.
const RANGES = ['1M', '3M', '6M', '1Y'];

interface HeroProps {
  /**
   * @deprecated 2026-06-10 (XCUT-07). The fabricated benchmark line was
   * removed — there is no market-data feed, so this prop no longer has any
   * effect. Retained only so existing call sites keep type-checking; safe
   * to drop once every caller stops passing it.
   */
  showBenchmark?: boolean;
}

/** Map AumSnapshotView[] (newest-first) → GrowthPoint[] (oldest-first, quarterly marks). */
function snapshotsToGrowthSeries(snapshots: AumSnapshotView[]): GrowthPoint[] {
  // Reverse to chronological for the chart.
  const chrono = [...snapshots].sort((a, b) => a.month.localeCompare(b.month));
  const out: GrowthPoint[] = [];
  let prev = 0;
  for (let i = 0; i < chrono.length; i++) {
    const snap = chrono[i]!;
    const monthPart = snap.month.slice(5); // 'MM'
    const isQuarterEnd = monthPart === '03' || monthPart === '06' || monthPart === '09' || monthPart === '12';
    const qAmt = prev > 0 ? snap.valueZar - prev : 0;
    out.push({
      month: snap.month,
      value: snap.valueZar,
      q: isQuarterEnd,
      ...(isQuarterEnd && qAmt !== 0 ? { qLabel: `Q${Math.ceil(Number(monthPart) / 3)}`, qAmt } : {}),
    });
    prev = snap.valueZar;
  }
  return out;
}

/**
 * AFS-GRADING finding 7 — which date (if any) the hero may attach to the
 * headline value, and whether the "as at" strip renders at all. PURE + exported
 * so the rule is unit-tested without rendering the chart.
 *
 * · a dated valuation (`totalValueAsAt`) always wins — it is the date the money
 *   was worth that figure;
 * · an AFS-basis total with NO dated record shows the provenance chip and NO
 *   date. `lastUpdated` is the row's last recalculation, which is exactly the
 *   wrong answer here (it labelled 28 Feb 2025 statements "as at Aug 2026");
 * · a live-holdings (or unstamped legacy) total may still fall back to
 *   `lastUpdated` — for a rolling sum, "when we last recalculated" IS its as-at.
 */
export function resolveHeroAsAt(
  summary: { totalValueAsAt?: string | null; totalValueBasis?: 'holdings' | 'afs'; lastUpdated?: string } | undefined,
): { show: boolean; iso: string | null } {
  if (!summary) return { show: false, iso: null };
  if (summary.totalValueAsAt) return { show: true, iso: summary.totalValueAsAt };
  if (summary.totalValueBasis === 'afs') {
    // Chip only — never a date we cannot stand behind.
    return { show: true, iso: null };
  }
  const fallback = summary.lastUpdated;
  if (fallback) return { show: true, iso: fallback };
  return { show: !!summary.totalValueBasis, iso: null };
}

/**
 * AFS-GRADING finding 2 — the AFS "distributions to beneficiaries" total and the
 * financial year it belongs to, or null when there is nothing honest to show
 * (no AFS, no figure, or a zero). PURE + exported for unit tests.
 */
export function resolveAfsDistributions(
  summary: { afsFinancials?: { periodEndDate: string | null; distributionsToBeneficiaries?: number } | null } | undefined,
): { amountRand: number; fyLabel: string | null } | null {
  const afs = summary?.afsFinancials;
  const amountRand = afs?.distributionsToBeneficiaries;
  if (typeof amountRand !== 'number' || !Number.isFinite(amountRand) || amountRand <= 0) return null;
  // SA trust financial years are named by the year they END in (28/29 Feb).
  const year = afs?.periodEndDate?.slice(0, 4);
  return { amountRand, fyLabel: year && /^\d{4}$/.test(year) ? `FY${year}` : null };
}

export function Hero(_props: HeroProps = {}) {
  // `showBenchmark` is accepted for call-site compatibility but ignored —
  // the benchmark series was removed (XCUT-07). Reference it so the unused
  // parameter doesn't trip lint.
  void _props;
  const [range, setRange] = useState('1Y');
  const { data: summary, isLoading, isError, error } = useDashboardSummary();
  // A fresh account's summary 404s with NO_TRUSTS_FOR_USER — that is the
  // day-one state, not a failure. Rendering it as a red "Couldn't load" was
  // the first thing Paul saw on the 2026-07-18 prod walkthrough.
  const noTrustsYet =
    (error as { code?: string } | null | undefined)?.code === 'NO_TRUSTS_FOR_USER';
  const trustsQ = useTrusts();
  const trustsCount = trustsQ.data?.trusts.length ?? 0;
  const firstTrust = trustsQ.data?.trusts[0];
  const aumQ = useAumSnapshots(firstTrust?.id, { limit: 24 });
  const aumSnapshots = aumQ.data?.aumSnapshots ?? [];
  const growthSeries = snapshotsToGrowthSeries(aumSnapshots);

  // ESTATE ownership model (2026-07-11 Paul ruling — extends the day-1
  // "life-stage" reframe rather than replacing it). `estateValue` is the
  // server-computed headline: registered (across ALL the user's trusts) +
  // earmarked + person-owned — NEVER re-summed client-side when the field is
  // present (the backend is the one seam that holds both the trust list and
  // the asset sums; re-deriving it here risks silently dropping another
  // trust's registered value). Whenever the estate is worth MORE than the
  // primary trust's registered total — any earmarked or person-owned value at
  // all, not just the original "registered is exactly R0" day-1 corner — the
  // hero leads with the estate figure labelled "Your estate" and shows the
  // honest 3-way split ("R x registered to trusts · R y transfer in progress
  // · R z personal") instead of a lone registered number. The registered
  // figure (`totalValue`) stays the honest AUM anchor either way — it is
  // always the first split part, even at R0.
  //
  // Old-backend fallback (no `estateValue` field yet — a mixed deploy
  // window): reconstruct the pre-ESTATE-model day-1 estimate from
  // `claimedPendingValue` alone (no personal-estate figure existed then).
  // PER-FIRM VISIBILITY SCOPES (Paul + Beno, 2026-08-23). When the client has
  // withheld their total portfolio value from the firm whose session this is,
  // the backend OMITS the four estate figures and sets this flag.
  //
  // ⚠ THE FLAG IS LOAD-BEARING, not decoration. Without it the "old backend"
  // fallback below sees a missing `estateValue`, reconstructs one as
  // `totalValue + claimedPendingValue`, and prints it under the words "Your
  // estate" — a fabricated net-worth headline standing exactly where the client
  // asked for none. Withheld therefore pins the hero to its honest
  // trust-scoped mode: "Total trust value" off `totalValue`, the primary
  // trust's own registered figure, which the firm administers and may see.
  // 2026-08-25. Paul, reading this card while the act-as banner above it said
  // "Administering Paul": "here it says connect your fiduciary firm to lodge it
  // for you. What does this do? But I am already [connected]. What is your
  // meaning with this?" The card offered to connect a firm to a client who
  // plainly had one. It never asked.
  const grantsQ = useGrants();
  const activeGrants = (grantsQ.data?.grants ?? []).filter((g) => g.status === 'active');
  const administeringFirm = activeGrants.find((g) => g.accessLevel === 'manage');
  // POR-06, the remaining half. "Connect your fiduciary firm" is only true when
  // NO firm is connected. A firm holding a read-only grant is connected: it just
  // cannot lodge. Offering "connect" to that client repeats the original
  // complaint on a narrower path, so the two cases are told apart here.
  const readOnlyFirm = administeringFirm ? undefined : activeGrants[0];
  // TWO LINKS ON THE BOUNCE TARGET ITSELF (2026-08-27). This card renders on
  // /app/dashboard, which is where the /app guard sends a firm that tries to
  // open one of the client's own screens. So a firm acting as a client was
  // bounced to the dashboard and immediately offered "Ask them" (/app/inbox)
  // and "Connect your fiduciary firm" (/app/firm-access), both of which bounce
  // them straight back here. The gate is the guard's own list, read through
  // lib/authz/act-as-nav, so this card can never offer a door the guard shuts.
  const hiddenFromActingFirm = useHiddenFromActingFirm();
  const inboxHidden = hiddenFromActingFirm('/app/inbox');
  const firmAccessHidden = hiddenFromActingFirm('/app/firm-access');
  const netWorthWithheld = summary?.withheld?.netWorth === true;
  const registeredTotal = summary?.totalValue;
  const hasEstateFields = summary?.estateValue !== undefined;
  const legacyPending = summary?.claimedPendingValue ?? 0;
  const earmarkedValue = hasEstateFields ? (summary!.earmarkedValue ?? 0) : legacyPending;
  const personOwnedValue = hasEstateFields ? (summary!.personOwnedValue ?? 0) : 0;
  const estateValue = hasEstateFields
    ? summary!.estateValue!
    : (registeredTotal ?? 0) + legacyPending;
  // DEFECT A FIX (2026-07-12): the split's "registered to trusts" clause
  // needs the TENANT-WIDE registered figure, not `totalValue` (which stays
  // PRIMARY-TRUST-SCOPED). Prefer the server's own `registeredValue` field;
  // fall back to deriving it from the estate headline when an old backend
  // hasn't shipped the field yet (estateValue - earmarked - personOwned IS
  // the registered component, by construction) — outside estate mode (no
  // estate fields at all, a legacy backend) there is no tenant-wide figure
  // to reconcile against, so the primary-scoped total is as good as it gets.
  const registeredTenantValue = hasEstateFields
    ? (summary!.registeredValue ?? (estateValue - earmarkedValue - personOwnedValue))
    : (registeredTotal ?? 0);
  // DEFECT A FIX: gate estate mode on REAL non-registered value existing —
  // earmarked or person-owned — rather than "estateValue > totalValue" (which
  // stood in the PRIMARY-TRUST-SCOPED `totalValue` for the tenant-wide
  // registered figure; a client with 2+ registered trusts and nothing
  // earmarked/personal then saw a spurious "Your estate" headline whose split
  // couldn't reconcile against it). A purely-registered multi-trust book now
  // correctly stays in the honest "Total trust value" mode.
  const estateMode =
    !netWorthWithheld && summary !== undefined && earmarkedValue + personOwnedValue > 0;

  // Day-one value line (Paul's ruling, 2026-07-15): with <2 real snapshots,
  // draw a synthetic R0→current-value line per selected range (first-class
  // chart styling + an honest "First valuation" annotation) instead of
  // placeholder text + empty space. The line rises to the SAME figure the
  // hero headline shows (estate value in estate mode); real history takes
  // over untouched at ≥2 snapshots. See growth-series.ts for the rules.
  const headlineValue = estateMode ? estateValue : (summary?.totalValue ?? 0);
  const asOfIso = summary?.lastUpdated;
  const asOfDate = asOfIso && !Number.isNaN(Date.parse(asOfIso)) ? new Date(asOfIso) : null;
  // AFS-GRADING finding 7 — see the "as at" block below. `totalValueAsAt` is the
  // valuation's OWN date; `lastUpdated` is only the row's recalculation stamp and
  // may stand in ONLY for a live-holdings total.
  const heroAsAt = resolveHeroAsAt(summary);
  const afsDistributions = resolveAfsDistributions(summary);
  const growthDisplay = resolveGrowthDisplay({
    realSeries: growthSeries,
    range,
    currentValueZar: headlineValue,
    asOf: asOfDate,
  });

  // POR-03 (M3 A7) — "if I change the times here or the durations, as you can
  // see, only the date changed. Nothing else changes."
  //
  // He was right, and only in one state. On a REAL series the tabs genuinely
  // refilter (growth-chart slices the window). On the DAY-ONE synthetic line
  // there are exactly two points, R0 at the range start and today's value, so
  // 1M/3M/6M/1Y all draw the same two points and only the x-axis start label
  // moves. That is a control that changes its own label and nothing else, which
  // the register calls worse than no control.
  //
  // So the tabs are shown only when there is a real series behind them. The
  // day-one line itself is untouched: it is Paul's own 2026-07-15 ruling and
  // this row is about the RANGE CONTROL, not the chart.
  const rangeTabsUseful = growthDisplay.kind === 'real';

  // POR-01 (M1 §H) — the cross-trust total, on a screen that only ever showed
  // one trust's.
  //
  // `summary.totalValue` is the PRIMARY trust's registered figure, and so are
  // the "as at" date, the year-on-year pill and the chart beside them. Swapping
  // the headline for the tenant-wide sum would leave all three describing a
  // different number than the one above them, which is the defect class this
  // register is mostly made of. `summary.registeredValue` is the honest
  // tenant-wide figure (dashboard-summary sums every visible trust), so it goes
  // in as its OWN line, scoped in words, with a way through to the list.
  //
  // Only when it says something the headline does not: more than one trust, and
  // a figure the client is entitled to see.
  const registeredAcross = summary?.registeredValue;
  const showCrossTrustTotal =
    !netWorthWithheld &&
    trustsCount > 1 &&
    typeof registeredAcross === 'number' &&
    registeredAcross > 0;

  return (
    <section className="hero">
      <div className="hero-grid">
        <div className="hero-left">
          <div>
            <div className="trust-tag">
              <div className="seal">e</div>
              <div className="trust-tag-name">
                {/* Day one: a warm placeholder beats a bare em-dash next to the seal. */}
                {displayLegalName(summary?.trustName) || (isLoading ? 'Loading…' : noTrustsYet ? 'Your first trust' : 'Not recorded')}
              </div>
            </div>
            <div className="hero-label">{estateMode ? 'Your estate' : 'Total trust value'}</div>
            <div className="hero-value">
              {/* Day one (NO_TRUSTS_FOR_USER) renders the honest R0 the
                  synthetic growth line rises from — NOT the error "Not valued"
                  with aria "Unable to load" (2026-07-19 miss-hunt: the
                  2026-07-18 day-one fixes gated the chart + chip on
                  noTrustsYet but left the VALUE on the raw error branch). */}
              <HeroValue
                isLoading={isLoading}
                isError={isError && !noTrustsYet}
                totalValue={
                  estateMode
                    ? estateValue
                    : (summary?.totalValue ?? (noTrustsYet ? 0 : undefined))
                }
              />
            </div>
            {/* ESTATE ownership model: the SPLIT line. The total above is the
                estate headline in estate mode, or the honest registered/
                verified total otherwise — it never silently absorbs earmarked
                or personal value. In estate mode this line spells out exactly
                what makes up that headline (registered — always shown, even
                at R0, as the honest AUM anchor — then earmarked / personal,
                zero-suppressed). Absent outside estate mode: the registered
                total already speaks for itself. */}
            {netWorthWithheld ? (
              /* PER-FIRM VISIBILITY SCOPE — the client has withheld their total
                 portfolio value from this firm. Say so plainly, in place of the
                 split. Silence would read as "no estate"; a zero would read as
                 "an empty estate". Neither is true, and on a fiduciary surface
                 the difference between "hidden" and "nothing" is the whole
                 point. The trust figure above is unaffected. */
              <div
                className="hero-pending"
                data-testid="hero-networth-withheld"
                style={{
                  marginTop: 8,
                  fontSize: 13,
                  color: 'var(--ink-3)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <Icon name="lock" size={13} stroke={2} />
                <span>
                  Total portfolio value withheld by the client. You can see the
                  trusts you administer.
                </span>
              </div>
            ) : summary && estateMode ? (
              <>
                <div
                  className="hero-pending"
                  data-testid="hero-estate-split"
                  style={{
                    marginTop: 8,
                    fontSize: 13,
                    color: 'var(--ink-3)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <Icon name="clock" size={13} stroke={2} />
                  <span>
                    {estateSplitParts({
                      registeredRand: registeredTenantValue,
                      earmarkedRand: earmarkedValue,
                      personOwnedRand: personOwnedValue,
                    }).map((p, i, arr) => (
                      <span key={p.key}>
                        <b style={{ fontWeight: 600, color: 'var(--ink-2, var(--ink))' }}>
                          {formatRand(p.valueRand)}
                        </b>{' '}
                        {p.label}
                        {i < arr.length - 1 ? ' · ' : ''}
                      </span>
                    ))}
                  </span>
                </div>
                {/* B13 — don't dead-end the pending state. A sole owner with no
                    firm needs a real next step: register it themselves (we
                    describe the lodgement-pack path — the pack GENERATOR itself
                    is out of scope) or delegate to a firm via /app/firm-access.
                    Only relevant when something is actually earmarked — a
                    personal-only estate has nothing to lodge with the Master. */}
                {earmarkedValue > 0 ? (
                  <div
                    className="hero-pending-cta"
                    data-testid="hero-pending-cta"
                    style={{
                      marginTop: 6,
                      marginLeft: 19,
                      fontSize: 12,
                      color: 'var(--ink-3)',
                      lineHeight: 1.55,
                      maxWidth: 460,
                    }}
                  >
                    {/* Beno, 2026-07-30: this line led with "Register it
                        yourself", which reads as Elreign competing with the
                        fiduciary firms we sell THROUGH — he flagged it as
                        likely to cause resistance with our channel partners.
                        The firm now leads; self-lodgement stays available (it
                        is genuinely possible, and hiding it would be the
                        dishonest fix) but sits second, as the fallback. */}
                    Not yet lodged with the Master.{' '}
                    {administeringFirm ? (
                      <>
                        <strong style={{ color: 'var(--ink)' }}>
                          {administeringFirm.firmDisplayName}
                        </strong>{' '}
                        {inboxHidden ? (
                          /* A firm acting as this client is reading this. It is
                             the firm named a few words earlier, so "ask them"
                             was always odd, and the link went to the client's
                             own inbox, which this firm may not open. State the
                             fact and stop there. */
                          <span data-testid="hero-lodgement-acting-firm">
                            administers this and can lodge it.
                          </span>
                        ) : (
                          <>
                            administers this for you and can lodge it.{' '}
                            <Link
                              to="/app/inbox"
                              style={{
                                color: 'var(--primary-700)',
                                fontWeight: 600,
                                textDecoration: 'underline',
                                textUnderlineOffset: 2,
                              }}
                            >
                              Ask them
                            </Link>
                            , or, if you would rather do it yourself, we&rsquo;ll
                            prepare your Master lodgement pack.
                          </>
                        )}
                      </>
                    ) : readOnlyFirm ? (
                      /* POR-06 — connected, but on a view-only grant. Name the
                         firm and the actual gap. */
                      <span data-testid="hero-lodgement-read-only-firm">
                        <strong style={{ color: 'var(--ink)' }}>
                          {readOnlyFirm.firmDisplayName}
                        </strong>{' '}
                        has view-only access, so they cannot lodge it for you.
                        {firmAccessHidden ? null : (
                          <>
                            {' '}
                            <Link
                              to="/app/firm-access"
                              style={{
                                color: 'var(--primary-700)',
                                fontWeight: 600,
                                textDecoration: 'underline',
                                textUnderlineOffset: 2,
                              }}
                            >
                              Give them management access
                            </Link>{' '}
                            if you would like them to.
                          </>
                        )}
                      </span>
                    ) : firmAccessHidden ? (
                      /* Same posture, no grant on file. Firm access is the page
                         on which the client grants and revokes this very firm,
                         so it is never offered to the firm. */
                      <span data-testid="hero-lodgement-acting-firm">
                        No fiduciary firm is connected to lodge it yet.
                      </span>
                    ) : (
                      <>
                        <Link
                          to="/app/firm-access"
                          style={{
                            color: 'var(--primary-700)',
                            fontWeight: 600,
                            textDecoration: 'underline',
                            textUnderlineOffset: 2,
                          }}
                        >
                          Connect your fiduciary firm
                        </Link>{' '}
                        to lodge it for you, or, if you would rather do it
                        yourself, we&rsquo;ll prepare your Master lodgement pack.
                      </>
                    )}
                  </div>
                ) : null}
              </>
            ) : null}
            {/* POR-01 — the total across every trust the client is in, said in
                words that name its scope so it can never be mistaken for the
                headline above it. */}
            {showCrossTrustTotal ? (
              <div
                data-testid="hero-cross-trust-total"
                style={{
                  marginTop: 8,
                  fontSize: 13,
                  color: 'var(--ink-3)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  flexWrap: 'wrap',
                }}
              >
                <Icon name="trust" size={13} stroke={2} />
                <span>
                  <b style={{ fontWeight: 600, color: 'var(--ink-2, var(--ink))' }}>
                    {formatRand(registeredAcross)}
                  </b>{' '}
                  registered across your {trustsCount} trusts.{' '}
                  <Link
                    to="/app/trusts"
                    style={{
                      color: 'var(--primary-700)',
                      fontWeight: 600,
                      textDecoration: 'underline',
                      textUnderlineOffset: 2,
                    }}
                  >
                    See each one
                  </Link>
                </span>
              </div>
            ) : null}
            <div className="hero-deltas">
              {summary ? (
                // F6 (YoY zero-guard): a fresh trust has no 12-month-old data —
                // its yoyPct/yoyAmount arrive as 0/0. "+0.0% / +R 0 over 12
                // months" would be an invented return, so suppress the pill and
                // say so honestly.
                summary.yoyPct === 0 && summary.yoyAmount === 0 ? (
                  // Warmer than "No 12-month comparison yet" (Paul's day-one
                  // ruling) but still honest: the picture is being built —
                  // each nightly snapshot adds a month.
                  <span className="delta-amt" style={{ color: 'var(--ink-3)' }}>
                    Building your 12-month picture
                  </span>
                ) : (
                  <>
                    <span className={'delta-pill' + (summary.yoyPct < 0 ? ' neg' : '')}>
                      <Icon
                        name={summary.yoyPct < 0 ? 'arrow-down' : 'arrow-up'}
                        size={12}
                        stroke={2.25}
                      />
                      {summary.yoyPct > 0 ? '+' : ''}
                      {summary.yoyPct.toFixed(1)}%
                    </span>
                    <span className="delta-amt">
                      <b>
                        {summary.yoyAmount > 0 ? '+' : ''}
                        {formatRand(summary.yoyAmount)}
                      </b>{' '}
                      over 12 months
                    </span>
                  </>
                )
              ) : isError && !noTrustsYet ? (
                <span className="delta-amt" style={{ color: 'var(--negative)' }}>
                  Couldn&rsquo;t load the latest figures.
                </span>
              ) : noTrustsYet ? (
                <span className="delta-amt" style={{ color: 'var(--ink-3)' }} data-testid="hero-day-one">
                  Your picture starts with your first trust.
                </span>
              ) : (
                <span className="delta-amt" aria-hidden="true">
                  &nbsp;
                </span>
              )}
            </div>
            {/* F5: the total is a dated, provenanced fact — "as at when" plus
                what it reflects (AFS NAV vs live holdings sum) when the
                backend stamps totalValueBasis (Wave-1 B4; absent on an old
                backend → date only).

                AFS-GRADING finding 7 (2026-08-22): the date came from
                `summary.lastUpdated` — the row's last RECALCULATION stamp — so a
                net asset value read off 28 February 2025 statements was labelled
                "as at Aug 2026". A valuation and its date are one fact. The
                backend now sends `totalValueAsAt`, the valuation's OWN as-at
                date, and it is `null` whenever the backend cannot date the total
                honestly. `lastUpdated` survives only as the fallback for a
                LIVE-HOLDINGS total (where the recalculation stamp really is when
                the sum was taken) — never for an AFS one, which shows the
                provenance chip with no date until a dated record exists. */}
            {heroAsAt.show ? (
              <div
                style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, fontSize: 12.5, color: 'var(--ink-3)' }}
                data-testid="hero-as-at"
              >
                {heroAsAt.iso ? <>as at {formatAsAtShort(heroAsAt.iso)}</> : null}
                {summary?.totalValueBasis ? (
                  <ValueProvenance
                    label={summary.totalValueBasis === 'afs' ? 'AFS valuation' : 'Live holdings sum'}
                    compact
                  />
                ) : null}
              </div>
            ) : null}
            {/* AFS-GRADING finding 2: a confirmed R450 000 of beneficiary
                distributions reached NO surface at all. It is a YEAR-END TOTAL
                off the statements — not a payout Elreign made, scheduled or
                witnessed — so it must never appear in the distributions
                register or the quarterly rhythm. It belongs beside the value it
                came from, labelled with the statements it came from. */}
            {afsDistributions !== null ? (
              <div
                style={{ marginTop: 6, fontSize: 12.5, color: 'var(--ink-3)' }}
                data-testid="hero-afs-distributions"
              >
                <b style={{ fontWeight: 600, color: 'var(--ink-2, var(--ink))' }}>
                  {formatRand(afsDistributions.amountRand)}
                </b>{' '}
                distributed to beneficiaries
                {afsDistributions.fyLabel ? ` (per AFS ${afsDistributions.fyLabel})` : ' (per the annual financial statements)'}
              </div>
            ) : null}
          </div>

          <div>
            <div className="range-row">
              {rangeTabsUseful ? (
                <div className="range-tabs" role="tablist" data-testid="hero-range-tabs">
                  {RANGES.map((r) => (
                    <button
                      key={r}
                      role="tab"
                      aria-selected={r === range}
                      className={'range-tab' + (r === range ? ' active' : '')}
                      onClick={() => setRange(r)}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              ) : growthDisplay.kind === 'synthetic' ? (
                <span
                  data-testid="hero-range-single-valuation"
                  style={{ fontSize: 12.5, color: 'var(--ink-3)' }}
                >
                  One valuation on file. Time ranges appear once there is a second
                  month to compare it with.
                </span>
              ) : null}
            </div>
          </div>
        </div>

        <div className="hero-right">
          <div className="chart-wrap">
            {aumQ.isLoading || trustsQ.isLoading ? (
              // Chart-shaped shimmer, not "Loading chart…" text (Paul's
              // doubt-words ruling): the block fills the chart area so the
              // layout is already composed while the data lands.
              <div
                aria-busy="true"
                aria-label="Loading chart"
                role="status"
                style={{ position: 'absolute', inset: 0, display: 'flex' }}
              >
                <Skeleton
                  testId="hero-chart-skeleton"
                  width="100%"
                  height="100%"
                  radius={12}
                  style={{ display: 'block', flex: 1 }}
                />
              </div>
            ) : aumQ.isError ? (
              <div style={{ padding: 40, color: 'var(--negative)', fontSize: 13 }}>
                {mapError(aumQ.error, 'load')}
              </div>
            ) : growthDisplay.kind === 'empty' ? (
              // Genuinely nothing valued yet (headline R0, no snapshots) —
              // keep the honest placeholder; an R0→R0 line says less.
              <div
                style={{
                  padding: 40,
                  color: 'var(--ink-3)',
                  fontSize: 13,
                  lineHeight: 1.55,
                  textAlign: 'center',
                }}
              >
                Estate growth appears as valuations update.
              </div>
            ) : growthDisplay.kind === 'synthetic' ? (
              // Day-one value line: R0 at the range start → the headline value
              // this month, same line/area styling as the real chart, honest
              // "First valuation" annotation, and extra y-headroom so the
              // value sits comfortably in the upper-middle of the plot.
              <GrowthChart
                data={growthDisplay.points}
                range={range}
                headroom={growthDisplay.headroom}
                annotation={growthDisplay.annotation}
              />
            ) : (
              <GrowthChart data={growthDisplay.points} range={range} />
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function HeroValue({
  isError,
  totalValue,
}: {
  isLoading: boolean;
  isError: boolean;
  totalValue: number | undefined;
}) {
  if (totalValue !== undefined) {
    const intValue = Math.floor(totalValue);
    const cents = (totalValue - intValue).toFixed(2).slice(2);
    // Paul (2026-07-15): at hero scale the canonical U+202F thousands
    // separator all but vanishes and "3 850 000" reads as one blob. Widen the
    // separator gaps HERE ONLY (tables keep the narrow canonical form) by
    // wrapping each separator in a padded span — the text content is
    // unchanged, so copy/paste and tests still see the real character.
    const groups = fmtR(intValue).split(NNBSP);
    return (
      <>
        <span className="currency">R</span>
        {/* TYP-03 (grader, 5 Sep 2026): the same narrow space formatZarCents
            puts after the R, so this value and the Assets tab read alike. */}
        <span className="thousands-sep">{NNBSP}</span>
        {groups.map((g, i) => (
          <Fragment key={i}>
            {i > 0 && <span className="thousands-sep">{NNBSP}</span>}
            {g}
          </Fragment>
        ))}
        {/* Canonical SA decimal separator is a comma — "R 1 234 567,89"
            (CUX-1). The cents are rendered as a separate superscript span. */}
        <span className="cents">,{cents}</span>
      </>
    );
  }
  if (isError) {
    return (
      <span className="currency" aria-label="Unable to load">
        Not valued
      </span>
    );
  }
  return (
    <span aria-live="polite" style={{ opacity: 0.45 }}>
      <span className="currency">R</span>
      &nbsp;&nbsp;Not valued
    </span>
  );
}
